import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { warehouseService } from '../../services/warehouseService';
import { productService } from '../../services/productService';
import { useLocation } from '../../contexts/LocationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  AlertBanner,
  TabSwitcher,
  ProductSelectionModal,
  HistoryPanel,
  SelectedProductsTable,
  DetailsModal,
  StatusBadge,
  useWarehouses,
  useProductSearch,
  useSelectedProducts,
} from './WarehouseDocumentBase';

const InternalReceipt = () => {
  const { locationId } = useLocation();
  const { hasPermission } = usePermissions();
  const canAddProducts = hasPermission('magazyn', 'przyjecia', locationId);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0]);

  // Magazyny
  const { warehouses, selectedWarehouse, setSelectedWarehouse } = useWarehouses(locationId);

  // Produkty z bazy (nie ze stanu magazynowego - PW moze przyjac dowolny produkt)
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const loadProducts = useCallback(async (search = '') => {
    setLoadingProducts(true);
    try {
      const result = await productService.searchProducts({
        search: search,
        limit: 50
      });
      if (result && result.data && result.data.products) {
        setProducts(result.data.products);
      } else if (result && result.products) {
        setProducts(result.products);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Błąd ładowania produktów:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Wyszukiwanie z debounce
  useProductSearch(showProductModal, searchTerm, loadProducts);

  // Wybrane produkty
  const {
    selectedProducts,
    addProduct: baseAddProduct,
    updateQuantity: updateProductQuantity,
    updateReason: updateProductReason,
    removeProduct,
    clearProducts
  } = useSelectedProducts(setError, { enforceMaxQuantity: false });
  // PW = Przyjecie Wewnetrzne (DODAJEMY do stanu), wiec brak gornego limitu
  // (inaczej dla produktow z ujemnym/zerowym stanem nie da sie dodac NIC).

  // Historia PW
  const [receipts, setReceipts] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal szczegolow PW
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (activeTab === 'history' && locationId) {
      loadReceipts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (locationId && activeTab === 'history') {
      loadReceipts();
    }
  }, [locationId]);

  const loadReceipts = async () => {
    try {
      const result = await warehouseService.getInternalReceipts({
        date: dateFilter,
        status: statusFilter,
        location_id: locationId
      });
      if (result.success) {
        setReceipts(result.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania historii PW:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadReceipts();
    }
  }, [dateFilter, statusFilter]);

  const handleViewDetails = async (receipt) => {
    setSelectedReceipt(receipt);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    try {
      const result = await warehouseService.getInternalReceiptDetails(receipt.id);
      if (result.success) {
        setReceiptDetails(result.data);
      } else {
        setError('Nie udało się pobrać szczegółów dokumentu');
      }
    } catch (err) {
      console.error('Błąd pobierania szczegółów PW:', err);
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePrint = async (receipt) => {
    try {
      const response = await api.get(`/warehouse/internal-receipt/${receipt.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Blad generowania PDF dokumentu PW.');
    }
  };

  // Wczytywanie ujemnych stanów
  const [loadingNegative, setLoadingNegative] = useState(false);

  const loadNegativeStockProducts = async () => {
    setLoadingNegative(true);
    try {
      const res = await api.get(`/warehouse/negative-stock?location_id=${locationId}`);
      const items = res.data?.data || [];
      if (items.length === 0) {
        toast.success('Brak produktów z ujemnym stanem');
        setLoadingNegative(false);
        return;
      }
      // Dodaj wszystkie produkty z ujemnym stanem naraz (nie w pętli)
      const existingIds = new Set(selectedProducts.map(p => p.id));
      const newProducts = items
        .filter(item => !existingIds.has(item.id))
        .map(item => ({
          id: item.id,
          name: item.nazwa,
          nazwa: item.nazwa,
          barcode: item.ean || item.kod_produktu || '',
          stock_quantity: item.stan_aktualny,
          unit: 'szt',
          quantity: Math.abs(parseFloat(item.stan_aktualny) || 1),
          reason: 'Wyrównanie ujemnego stanu magazynowego',
          maxQuantity: 99999
        }));

      if (newProducts.length > 0) {
        clearProducts();
        newProducts.forEach(p => baseAddProduct(p, {
          quantity: p.quantity,
          reason: p.reason,
          maxQuantity: 99999  // PW nie ma limitu — przyjmujemy dowolną ilość
        }));
      }
      toast.success(`Wczytano ${newProducts.length} produktów z ujemnym stanem`);
    } catch (err) {
      console.error('Błąd wczytywania ujemnych stanów:', err);
      toast.error('Błąd wczytywania ujemnych stanów');
    } finally {
      setLoadingNegative(false);
    }
  };

  const addProduct = (product) => {
    baseAddProduct(product);
    setShowProductModal(false);
  };

  const submitInternalReceipt = async () => {
    if (loading) return;
    if (selectedProducts.length === 0) {
      setError('Dodaj przynajmniej jeden produkt');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const mappedProducts = selectedProducts.map(p => ({
        product_id: p.id,
        quantity: Math.max(1, Math.abs(parseInt(p.quantity) || 1)),
        reason: p.reason || 'Przyjęcie wewnętrzne'
      }));
      const result = await warehouseService.createInternalReceipt({
        products: mappedProducts,
        location_id: locationId,
        document_date: documentDate
      });

      if (result.success) {
        setSuccess('Przyjęcie wewnętrzne zostało zarejestrowane pomyślnie');
        clearProducts();
        loadReceipts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Błąd podczas rejestrowania przyjęcia');
      }
    } catch (err) {
      console.error('Błąd PW:', err);
      // Timeout rozpoznajemy po fladze isTimeout (interceptor kasuje err.code).
      // Zapis PW moze byc juz w trakcie realizacji po stronie serwera -> NIE
      // czyscimy listy i NIE zachecamy do ponawiania, zeby nie utworzyc duplikatu.
      const isTimeout = err?.isTimeout === true || /timeout|czas oczekiwania/i.test(err?.message || '');
      if (isTimeout) {
        setError('Serwer nie odpowiedział w wyznaczonym czasie — zapis może trwać w tle. Nie ponawiaj wysyłki: odśwież listę PW poniżej, aby sprawdzić, czy przyjęcie zostało zarejestrowane.');
      } else {
        setError(err?.response?.data?.error || err?.message || 'Błąd połączenia z serwerem');
      }
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    { label: 'Numer dokumentu' },
    { label: 'Data' },
    { label: 'Produkty' },
    { label: 'Status' },
    { label: 'Utworzył' },
    { label: 'Akcje', align: 'center' },
  ];

  const renderHistoryRow = (receipt) => {
    const isDeleted = receipt.status === 'deleted';
    return (
    <tr key={receipt.id} style={{ opacity: isDeleted ? 0.5 : 1 }}>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textDecoration: isDeleted ? 'line-through' : 'none' }}>
        <strong>{receipt.document_number}</strong>
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        {new Date(receipt.receipt_date).toLocaleDateString('pl-PL')}
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        <span style={{
          padding: '0.1rem 0.3rem',
          fontSize: '10px',
          backgroundColor: 'var(--cyan)',
          color: '#000',
          borderRadius: '3px'
        }}>
          {receipt.items_count || 0} poz.
        </span>
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        {isDeleted ? (
          <span style={{ padding: '0.1rem 0.3rem', fontSize: '10px', backgroundColor: 'var(--danger)', color: '#fff', borderRadius: '3px' }}>Usunięte</span>
        ) : (
          <StatusBadge status={receipt.status} completedLabel="Zakończone" pendingLabel="Oczekujące" />
        )}
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '10px' }}>
        {receipt.created_by || 'System'}
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
        <button
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            border: '1px solid var(--info)',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--info)',
            cursor: 'pointer',
            marginRight: '0.15rem'
          }}
          onClick={() => handleViewDetails(receipt)}
          title="Zobacz szczegóły"
        >
          <i className="fas fa-eye"></i>
        </button>
        <button
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            border: '1px solid var(--text-muted)',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text-muted)',
            cursor: 'pointer'
          }}
          onClick={() => handlePrint(receipt)}
          title="Drukuj"
        >
          <i className="fas fa-print"></i>
        </button>
        {!isDeleted && (
          <button
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              border: '1px solid var(--danger)',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--danger)',
              cursor: 'pointer',
              marginLeft: '0.15rem'
            }}
            onClick={async () => {
              if (!window.confirm(`Czy na pewno chcesz usunąć PW ${receipt.document_number}?\nStany magazynowe zostaną cofnięte.`)) return;
              try {
                await api.delete(`/warehouse/internal-receipt/${receipt.id}`);
                toast.success(`PW ${receipt.document_number} usunięte, stany cofnięte`);
                loadReceipts();
              } catch (e) {
                toast.error(e?.response?.data?.error || 'Błąd usuwania PW');
              }
            }}
            title="Usuń PW i cofnij stany"
          >
            <i className="fas fa-trash"></i>
          </button>
        )}
      </td>
    </tr>
  );
  };

  return (
    <div style={{ fontSize: '12px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <span style={{ fontWeight: '600', fontSize: '13px' }}>📥 Przyj{'ę'}cie Wewn{'ę'}trzne (PW)</span>
      </div>

      <TabSwitcher
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          { key: 'new', label: 'Nowe PW', icon: 'fas fa-plus', activeColor: 'var(--success)' },
          { key: 'history', label: 'Historia PW', icon: 'fas fa-history', activeColor: 'var(--purple)' },
        ]}
      />

      <AlertBanner type="error" message={error} onClose={() => setError('')} />
      <AlertBanner type="success" message={success} onClose={() => setSuccess('')} />

      {activeTab === 'new' && (
        <>
          {canAddProducts && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <button
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--danger)', color: 'var(--danger)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                onClick={loadNegativeStockProducts}
                disabled={loadingNegative}
              >
                <i className="fas fa-minus-circle" style={{ fontSize: '10px' }}></i>
                {loadingNegative ? 'Ładowanie...' : 'Wczytaj ujemne stany'}
              </button>
              <button
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--info)', color: 'var(--info)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                onClick={() => setShowProductModal(true)}
              >
                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                Dodaj produkt
              </button>
            </div>
          )}

          <SelectedProductsTable
            products={selectedProducts}
            onQuantityChange={updateProductQuantity}
            onReasonChange={updateProductReason}
            onRemove={removeProduct}
            onSubmit={submitInternalReceipt}
            loading={loading}
            tableTitle="Produkty do przyjęcia"
            submitLabel="Zatwierdź przyjęcie"
            submitIcon="fas fa-check"
            submitColor="var(--success)"
            showStock={false}
            showReason={true}
            reasonPlaceholder="Powód przyjęcia..."
            reasonRequired={false}
            documentDate={documentDate}
            onDocumentDateChange={setDocumentDate}
          />
        </>
      )}

      {activeTab === 'history' && (
        <HistoryPanel
          items={receipts}
          columns={historyColumns}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={loadReceipts}
          emptyMessage="Brak przyjęć wewnętrznych"
          title="Historia Przyjęć Wewnętrznych"
          statusOptions={[
            { value: 'all', label: 'Wszystkie' },
            { value: 'completed', label: 'Zakończone' },
            { value: 'pending', label: 'Oczekujące' },
          ]}
          renderRow={renderHistoryRow}
        />
      )}

      {/* Product selection modal */}
      <ProductSelectionModal
        show={showProductModal}
        onClose={() => setShowProductModal(false)}
        products={products}
        loadingProducts={loadingProducts}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedProducts={selectedProducts}
        onAddProduct={addProduct}
        warehouses={warehouses}
        selectedWarehouse={selectedWarehouse}
        onWarehouseChange={setSelectedWarehouse}
        gradient="linear-gradient(135deg, var(--success), var(--cyan))"
        spinnerColor="var(--success)"
        addButtonColor="var(--success)"
        addButtonHoverColor="var(--info)"
        icon={'📦'}
        title="Wybierz produkt do przyjęcia"
        subtitle="PW - Przyjęcie wewnętrzne"
        warehouseLabel={'🏪 Magazyn docelowy'}
        showStock={true}
        hintBg="var(--info-light)"
        hintColor="var(--info)"
        hintIcon={'💡'}
        hintText="Przyjęcie wewnętrzne zwiększa stan magazynowy. Wybierz magazyn docelowy i produkty do przyjęcia."
      />

      {/* Details modal */}
      <DetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setReceiptDetails(null);
          setSelectedReceipt(null);
        }}
        title={`📋 Szczegóły Przyjęcia Wewnętrznego`}
        headerBg="var(--bg)"
        maxWidth="800px"
      >
        {loadingDetails ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid var(--border)',
              borderTop: '4px solid var(--success)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>{'Ł'}adowanie szczeg{'ółó'}w...</p>
          </div>
        ) : (
          <>
            {/* Header info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: 'var(--bg)',
              borderRadius: '0.375rem'
            }}>
              <div>
                <small style={{ color: 'var(--text-muted)' }}>Numer dokumentu</small>
                <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                  {selectedReceipt?.document_number || '-'}
                </div>
              </div>
              <div>
                <small style={{ color: 'var(--text-muted)' }}>Data</small>
                <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                  {selectedReceipt?.receipt_date ? new Date(selectedReceipt.receipt_date).toLocaleDateString('pl-PL') : '-'}
                </div>
              </div>
              <div>
                <small style={{ color: 'var(--text-muted)' }}>Status</small>
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                    backgroundColor: selectedReceipt?.status === 'completed' ? 'var(--success-light)' : 'var(--warning-light)',
                    color: selectedReceipt?.status === 'completed' ? 'var(--success)' : 'var(--warning)'
                  }}>
                    {selectedReceipt?.status === 'completed' ? 'Zakończone' : 'Oczekujące'}
                  </span>
                </div>
              </div>
              <div>
                <small style={{ color: 'var(--text-muted)' }}>Utworzy{'ł'}</small>
                <div style={{ fontWeight: '500' }}>
                  {selectedReceipt?.created_by || 'System'}
                </div>
              </div>
            </div>

            {/* Products list */}
            <h6 style={{ marginBottom: '0.75rem', fontWeight: '600' }}>📦 Produkty w dokumencie</h6>
            {receiptDetails?.items && receiptDetails.items.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Produkt</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', width: '100px' }}>Ilo{'ść'}</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Pow{'ó'}d</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptDetails.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--bg)' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ fontWeight: '500' }}>{item.product_name || `Produkt #${item.product_id}`}</div>
                        {item.product_code && (
                          <small style={{ color: 'var(--text-muted)' }}>{item.product_code}</small>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--info-light)',
                          borderRadius: '0.25rem',
                          fontWeight: '600',
                          color: 'var(--info)'
                        }}>
                          +{item.quantity}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.reason || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--text-muted)',
                backgroundColor: 'var(--bg)',
                borderRadius: '0.375rem'
              }}>
                Brak pozycji w dokumencie
              </div>
            )}

            {/* Summary */}
            {receiptDetails?.items && receiptDetails.items.length > 0 && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--success-light)',
                borderRadius: '0.375rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: '500', color: 'var(--success)' }}>
                  Suma pozycji: {receiptDetails.items.length}
                </span>
                <span style={{ fontWeight: '600', color: 'var(--success)' }}>
                  {'Łą'}czna ilo{'ść'}: +{receiptDetails.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                </span>
              </div>
            )}
          </>
        )}
      </DetailsModal>
    </div>
  );
};

export default InternalReceipt;
